import React from "react";
import axios, { AxiosError, AxiosResponse } from "axios";
import { makeAutoObservable, runInAction } from "mobx";
import { User } from "oidc-client-ts";
import { AuthProvider, useAuth } from "react-oidc-context";
import { AuthUrl, BaseUrl, redirectUrl, tenant } from "./constants";
import * as Models from "../models/models";
import { QueryClient } from "@tanstack/react-query";

export class AuthStore {
  private _queryClient: QueryClient;
  constructor(queryClient: QueryClient) {
    this._queryClient = queryClient;
    makeAutoObservable(this);
  }

  user: User | null = null;
  users: Models.User[] = [];
  usersState: Models.LoadState = Models.LoadState.NotLoaded;

  get Tenant() {
    return tenant;
  }

  setUser = (user: User | null) => {
    runInAction(() => {
      this.user = user;
    });
  };

  get ApiCaller() {
    return axios.create({
      baseURL: `${BaseUrl()}api`,
      headers: {
        "x-tenant": this.Tenant,
        authorization: `Bearer ${this.user?.access_token}`,
      },
    });
  }
  get AuthCaller() {
    return axios.create({
      baseURL: `${AuthUrl()}/api`,
      headers: {
        "x-tenant": this.Tenant,
        authorization: `Bearer ${this.user?.access_token}`,
      },
    });
  }
  get HasToken() {
    return !!this?.user?.access_token;
  }

  async InitializeUsers(isUpdate?: boolean) {
    if (this.usersState === Models.LoadState.Loaded && isUpdate == null) {
      return;
    }
    this.usersState = Models.LoadState.Loading;
    try {
      const { data }: AxiosResponse<Models.User[]> = await this.AuthCaller.get(`/User/AllUsers`);
      runInAction(() => {
        this.users = [...data];
        this.usersState = Models.LoadState.Loaded;
      });
    } catch (e) {
      runInAction(() => {
        this.users = [];
        this.usersState = Models.LoadState.Error;
      });
    }
  }
  async AddUser(user: Models.User) {
    try {
      const { data }: AxiosResponse<Models.User> = await this.AuthCaller.post(`/User/AddUser`, user);
      runInAction(() => {
        this.users.push(data);
        this._queryClient.invalidateQueries([Models.QueryKeys.Users]);
      });
      return {
        error: false,
        data: data,
      };
    } catch (e) {
      return {
        error: true,
        data: (e as AxiosError).response?.data,
      };
    }
  }
  async GetAllUsers() {
    if (this.usersState != Models.LoadState.Loaded) {
      await this.InitializeUsers();
    }
    return this.users;
  }

  GetUserById(userId: string) {
    return this.users.find((u) => u.id === userId);
  }

  async DeleteUser(userId: string) {
    try {
      await this.AuthCaller.delete(`/User/DeleteUser/${userId}`);
      runInAction(() => {
        this.users = this.users.filter((u) => u.id !== userId);
        this._queryClient.invalidateQueries([Models.QueryKeys.Users]);
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  async LockUser(user: Models.User) {
    try {
      const { data }: AxiosResponse<Models.User> = await this.AuthCaller.patch(`/User/LockUser/${user.id}`, user);
      runInAction(() => {
        this.users = this.users.map((u) => {
          if (u.id === user.id) {
            return data; // <-- Use the updated user data from the response
          } else {
            return u;
          }
        });
        this._queryClient.invalidateQueries([Models.QueryKeys.Users]);
        this._queryClient.invalidateQueries([Models.QueryKeys.User]);
      });
      return {
        error: false,
        data: data,
      };
    } catch (e) {
      const errorData = e as AxiosError;
      return {
        error: true,
        data: errorData.response?.data ? errorData.response?.data : errorData.message,
      };
    }
  }

  async UpdateUser(user: Models.User) {
    try {
      const { data }: AxiosResponse<Models.User> = await this.AuthCaller.put(`/User/UpdateUser/${user.id}`, user);
      runInAction(() => {
        this.users = this.users.map((u) => {
          if (u.id === user.id) {
            return user;
          } else {
            return u;
          }
        });
        this._queryClient.invalidateQueries([Models.QueryKeys.Users]);
        this._queryClient.invalidateQueries([Models.QueryKeys.User]);
      });
      return {
        error: false,
        data: data,
      };
    } catch (e) {
      const errorData = e as AxiosError;
      return {
        error: true,
        data: errorData.response?.data ? errorData.response?.data : errorData.message,
      };
    }
  }
  async UpdateUserProfileImage(file: File, id: string) {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await this.AuthCaller.post(`/User/UpdateProfileImage/${id}`, formData);
      this._queryClient.invalidateQueries([Models.QueryKeys.ProfileImage]);
      return true;
    } catch (e) {
      return false;
    }
  }

  async DeleteUserProfileImage(id: string) {
    try {
      await this.AuthCaller.delete(`/User/DeleteProfileImage/${id}`);
      this._queryClient.invalidateQueries([Models.QueryKeys.ProfileImage]);
      return true;
    } catch (e) {
      return false;
    }
  }

  GetProfileImageUrl(id: string) {
    return `${AuthUrl()}/api/User/ProfileImage/${this.Tenant}/${id}?${Date.now()}`;
  }

  async SendGetInTouchEmail(emailData: { name: string; phoneNumber: string; email: string; subject: string; message: string; tenant?: string }) {
    emailData.tenant = this.Tenant;
    try {
      await this.AuthCaller.post(`/User/GetInTouch`, emailData);
      return true;
    } catch (e) {
      return false;
    }
  }
}

export const AuthContextProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const settings = {
    authority: AuthUrl(),
    redirect_uri: redirectUrl,
    client_id: "Arc360",
    client_secret: "963A8007-1301-4ECE-A714-D39A910A9888",
    acr_values: `tenant:${tenant}`,
    scope: "openid profile email roles Arc360",
    extraTokenParams: {
      acr_values: `tenant:${tenant}`,
    },
  };
  return <AuthProvider {...settings}>{children}</AuthProvider>;
};

export const useAuthContext = () => {
  return useAuth();
};
